// Constants
import { DEFAULT_CORRELATION_OPTIONS, DEFAULT_TIMESTAMPS_ROLLING_OPTIONS, SORTING_DIRECTION } from '@/utils/constants';
import { MIN_TIMESTAMP } from '../time';

// Types
import ComparisonItem from '@/types/comparison-item';
import RollingPerformance from '@/types/rolling-performance';
import RiskPeriods from '@/types/risk-periods';
import { CorrelationMatrixOptions, TimestampsForRollingsOptions } from '@/types/risk-metric';
import CorrelationData from '@/types/correlation-data';
import Timestamp from '@/types/timestamp';

// Utils
import { calculateCorrelationMatrix, getCommonTimestamps, getValidPeriods, trimRollingData } from '../calculation-utils';

/**
 * Generates a correlation matrix for a given comparison item and risk period.
 *
 * @param {ComparisonItem} comparisonItem - The item to compare.
 * @param {RiskPeriods} riskPeriod - The period of risk to consider.
 * @param {CorrelationMatrixOptions} [options=DEFAULT_CORRELATION_OPTIONS] - Optional settings for the correlation matrix.
 * @returns {CorrelationData[]} The correlation matrix for the given comparison item and risk period.
 *
 * @remarks
 * This function retrieves the valid periods for the given comparison item and options,
 * and calculates the correlation matrix based on the valid period's slice count.
 * If no valid period is found, an empty array is returned.
 *
 * @todo Store the correlation matrix in the store for more efficiency
 */
export const getCorrelationMatrix = (
  comparisonItem: ComparisonItem,
  riskPeriod: RiskPeriods,
  options: CorrelationMatrixOptions = DEFAULT_CORRELATION_OPTIONS,
): CorrelationData[] => {
  // Correlation matrix is using the same dates than the risk metrics (the last common date is the last date we have in all holdings and benchmarks)
  const riskMetricsEndTimestamp = comparisonItem.dates.risk?.endDate ? new Date(comparisonItem.dates.risk.endDate).getTime() : undefined;
  const { beginTimestamp, lastCommonTimestamp } = getTimestampsForRollings(comparisonItem, { ...options, forcedEndDate: riskMetricsEndTimestamp });
  const validPeriod = getValidPeriodsForItem(comparisonItem, { beginTimestamp, lastCommonTimestamp }, options).find(({ period }) => period === riskPeriod);

  if (!validPeriod) {
    return [];
  }

  return calculateCorrelationMatrix(
    getCorrelationDataInput(comparisonItem, { beginTimestamp, lastCommonTimestamp }, options.useBaseCurrency),
    validPeriod.sliceCount,
  );
};

/**
 * Retrieves the begin and last common timestamps for rolling performances of a comparison item.
 *
 * @param {ComparisonItem} comparisonItem - The comparison item containing holdings and performance data.
 * @param {TimestampsForRollingsOptions} [options=DEFAULT_TIMESTAMPS_ROLLING_OPTIONS] - Options for timestamp retrieval.
 * @returns {{ beginTimestamp: number, lastCommonTimestamp: number }} An object containing the begin and last common timestamps.
 *
 * @remarks
 * - If `useLongestHistory` is true in options, the minimum timestamp is used as the begin timestamp.
 * - If `overrideHoldings` is true in the comparison item and portfolio rollings are available, the timestamps from the portfolio rollings are used.
 * - The function assumes that holdings have a `rolling` property which is an array of `RollingPerformance`.
 *
 */
export const getTimestampsForRollings = (comparisonItem: ComparisonItem, options: TimestampsForRollingsOptions = DEFAULT_TIMESTAMPS_ROLLING_OPTIONS) => {
  const { useBaseCurrency } = options;
  const { overrideHoldings } = comparisonItem;

  const portfolioPerformance = useBaseCurrency ? comparisonItem.performanceBase : comparisonItem.performance;
  const portfolioMonthlyRollings = portfolioPerformance?.rolling?.monthly ?? [];
  const mustUsePortfolioRollings = overrideHoldings?.length && portfolioMonthlyRollings.length;

  // In the case of the overriden holdings (real rollings of the portfolio provided by the enterprise)
  // we want to use the timestamps of the portfolioRolling instead of each individual holdings
  if (mustUsePortfolioRollings) {
    return getPortfolioRollingsTimestamps(portfolioMonthlyRollings);
  }

  return getComparisonHoldingsTimestamps(comparisonItem, options);
};

const getPortfolioRollingsTimestamps = (portfolioMonthlyRollings: [Timestamp, number][]) => {
  const beginTimestamp = portfolioMonthlyRollings[0][0];
  const lastCommonTimestamp = portfolioMonthlyRollings[portfolioMonthlyRollings.length - 1][0];
  return { beginTimestamp, lastCommonTimestamp };
};

const getComparisonHoldingsTimestamps = (comparisonItem: ComparisonItem, options: TimestampsForRollingsOptions) => {
  const { useLongestHistory, useBaseCurrency, forcedEndDate } = options;

  const rollingKey = useBaseCurrency ? 'baseRolling' : 'rolling';
  const holdingsRollings = comparisonItem.holdings.map(holding => (holding[rollingKey] ?? []) as RollingPerformance);
  const beginTimestamp = useLongestHistory ? MIN_TIMESTAMP : getCommonTimestamps(holdingsRollings, SORTING_DIRECTION.ASC).monthly;
  const lastCommonTimestamp = forcedEndDate || getCommonTimestamps(holdingsRollings, SORTING_DIRECTION.DESC).monthly;

  return { beginTimestamp, lastCommonTimestamp };
};

/**
 * Retrieves the valid periods for a given comparison item based on the provided options.
 *
 * @param {ComparisonItem} comparisonItem - The item to compare, containing performance data.
 * @param {{ beginTimestamp: number, lastCommonTimestamp: number }} timestamps - The begin and last common timestamps for the comparison item.
 * @param {TimestampsForRollingsOptions} [options=DEFAULT_TIMESTAMPS_ROLLING_OPTIONS] - Options to determine the valid periods.
 * @param {boolean} options.useLongestHistory - Flag to use the longest available history for the comparison.
 * @param {boolean} options.commonInception - Flag to use the common inception date for the comparison.
 * @param {boolean} options.useBaseCurrency - Flag to use the baseRolling for the dates
 * @returns {ValidPeriods} The valid periods for the comparison item.
 */
export const getValidPeriodsForItem = (
  comparisonItem: ComparisonItem,
  timestamps: { beginTimestamp: Timestamp; lastCommonTimestamp: Timestamp },
  options: TimestampsForRollingsOptions = DEFAULT_TIMESTAMPS_ROLLING_OPTIONS,
) => {
  const { useLongestHistory, commonInception, useBaseCurrency } = options;
  const { beginTimestamp, lastCommonTimestamp } = timestamps;

  const portfolioPerformance = useBaseCurrency ? comparisonItem.performanceBase : comparisonItem.performance;
  const fullPortfolioMonthlyRollings = portfolioPerformance?.rolling?.monthly ?? [];

  const portfolioRollingLength = useLongestHistory
    ? fullPortfolioMonthlyRollings.length
    : trimRollingData(fullPortfolioMonthlyRollings, beginTimestamp, lastCommonTimestamp).length;

  return getValidPeriods(beginTimestamp, lastCommonTimestamp, portfolioRollingLength, commonInception?.startTimestamp);
};

/**
 * Generates correlation data input for a given comparison item.
 *
 * @param {ComparisonItem} comparisonItem - The comparison item containing holdings data.
 * @param {{ beginTimestamp: number, lastCommonTimestamp: number }} timestamps - The begin and last common timestamps for the comparison item.ß
 * @param {boolean} [useBaseCurrency=false] - Flag indicating whether to use base currency rolling data.
 * @returns {Array<{ _id: string, rolling: Array<any> }>} An array of objects containing holding IDs and their respective rolling data.
 * @throws {Error} Throws an error if base rolling data is missing when `useBaseCurrency` is true.
 */
export const getCorrelationDataInput = (
  comparisonItem: ComparisonItem,
  timestamps: { beginTimestamp: Timestamp; lastCommonTimestamp: Timestamp },
  useBaseCurrency = false,
) =>
  comparisonItem.holdings.map(holding => {
    const monthlyRollings = holding.rolling?.monthly;
    const monthlyBaseRollings = holding.baseRolling?.monthly;

    if (useBaseCurrency && !monthlyBaseRollings) {
      throw new Error('Base rolling data is missing');
    }

    const rollingData = useBaseCurrency ? monthlyBaseRollings : monthlyRollings;
    const trimmedRollingData = trimRollingData(rollingData, timestamps.beginTimestamp, timestamps.lastCommonTimestamp);
    return {
      _id: holding.id,
      rolling: [...(trimmedRollingData ?? [])],
    };
  });
